import React, { useState, useEffect, useRef } from "react";
import { MONTH_NAMES, MONTHS_SHORT, WEEKDAYS } from "../constants";
import { fetchHolidays } from "../api";
import { pad2, getISOWeek } from "../lib/dates";
import "./MiniCalendar.css";

// Module-level cache so popping open a date picker on the same year is instant
const holidayCache = {};

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

export default function MiniCalendar({ currentYear, currentMonth, onSelect, onClose, rangeStart, rangeEnd, hideToday }) {
  // view: "days" | "months" | "years"
  const [view, setView] = useState("days");
  const [viewYear, setViewYear] = useState(currentYear);
  const [viewMonth, setViewMonth] = useState(currentMonth);
  const [yearRangeStart, setYearRangeStart] = useState(currentYear - (currentYear % 12));
  const [holidays, setHolidays] = useState(holidayCache[currentYear] || {});
  const ref = useRef(null);

  useEffect(() => {
    if (holidayCache[viewYear] !== undefined) {
      setHolidays(holidayCache[viewYear]);
      return;
    }
    let cancelled = false;
    fetchHolidays(viewYear)
      .then((data) => {
        // Only cache successes — caching a failure would hide holiday markers
        // until a full reload, since the cache is checked before every fetch.
        holidayCache[viewYear] = data || {};
        if (!cancelled) setHolidays(data || {});
      })
      .catch(() => {
        // No cache write: the next mount / year switch retries the fetch.
        if (!cancelled) setHolidays({});
      });
    return () => { cancelled = true; };
  }, [viewYear]);

  useEffect(() => {
    if (typeof onClose !== "function") return; // inline (embedded) usage: no outside-close
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Highlight class for a day's ISO date when a range is active.
  const rangeClass = (iso) => {
    if (!rangeStart) return "";
    const other = rangeEnd || rangeStart;
    const lo = rangeStart < other ? rangeStart : other;
    const hi = rangeStart < other ? other : rangeStart;
    if (iso === lo && iso === hi) return " mini-cal-range-single";
    if (iso === lo) return " mini-cal-range-start";
    if (iso === hi) return " mini-cal-range-end";
    if (iso > lo && iso < hi) return " mini-cal-in-range";
    return "";
  };

  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();

  // --- YEARS VIEW ---
  if (view === "years") {
    const years = [];
    for (let y = yearRangeStart; y < yearRangeStart + 12; y++) {
      years.push(y);
    }
    return (
      <div className="mini-cal" ref={ref}>
        <div className="mini-cal-header">
          <button className="mini-cal-nav" onClick={() => setYearRangeStart(yearRangeStart - 12)}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>
          <span className="mini-cal-title">{yearRangeStart} – {yearRangeStart + 11}</span>
          <button className="mini-cal-nav" onClick={() => setYearRangeStart(yearRangeStart + 12)}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></button>
        </div>
        <div className="mini-cal-grid-4">
          {years.map((y) => (
            <button
              key={y}
              className={`mini-cal-cell ${y === todayYear && !hideToday ? "mini-cal-today" : ""}`}
              onClick={() => { setViewYear(y); setView("months"); }}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // --- MONTHS VIEW ---
  if (view === "months") {
    return (
      <div className="mini-cal" ref={ref}>
        <div className="mini-cal-header">
          <button className="mini-cal-nav" onClick={() => setViewYear(viewYear - 1)}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>
          <button className="mini-cal-title mini-cal-title-btn" onClick={() => { setYearRangeStart(viewYear - (viewYear % 12)); setView("years"); }}>
            {viewYear}
          </button>
          <button className="mini-cal-nav" onClick={() => setViewYear(viewYear + 1)}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></button>
        </div>
        <div className="mini-cal-grid-4">
          {MONTHS_SHORT.map((m, i) => (
            <button
              key={i}
              className={`mini-cal-cell ${viewYear === todayYear && i === todayMonth && !hideToday ? "mini-cal-today" : ""}`}
              onClick={() => { setViewMonth(i); setView("days"); }}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // --- DAYS VIEW ---
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  // Build rows (weeks) with KW numbers
  const rows = [];
  let dayCursor = 1;

  // First row may have empty cells before the 1st
  let firstRow = [];
  for (let i = 0; i < firstDay; i++) {
    firstRow.push(<span key={`empty-${i}`} className="mini-cal-empty" />);
  }
  for (let i = firstDay; i < 7 && dayCursor <= daysInMonth; i++, dayCursor++) {
    const d = dayCursor;
    const isToday = viewYear === todayYear && viewMonth === todayMonth && d === todayDate;
    const wd = new Date(viewYear, viewMonth, d).getDay();
    const iso = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(d)}`;
    const isWeekend = wd === 0 || wd === 6;
    const isHoliday = !!holidays[iso];
    // Same hideToday rule as the loop below — without it, a today that falls on
    // a weekend/holiday in the FIRST week row stayed ungreyed (looked marked).
    const offClass = (!isToday || hideToday) && (isWeekend || isHoliday) ? " mini-cal-off" : "";
    firstRow.push(
      <button
        key={d}
        className={`mini-cal-day ${isToday && !hideToday ? "mini-cal-today" : ""}${offClass}${rangeClass(iso)}`}
        onClick={() => onSelect(viewYear, viewMonth, d)}
        title={isHoliday ? holidays[iso] : undefined}
      >{d}</button>
    );
  }
  rows.push({ week: getISOWeek(new Date(viewYear, viewMonth, 1)), cells: firstRow });

  // Remaining rows
  while (dayCursor <= daysInMonth) {
    const rowStart = dayCursor;
    const row = [];
    for (let i = 0; i < 7 && dayCursor <= daysInMonth; i++, dayCursor++) {
      const d = dayCursor;
      const isToday = viewYear === todayYear && viewMonth === todayMonth && d === todayDate;
      const wd = new Date(viewYear, viewMonth, d).getDay();
      const iso = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(d)}`;
      const isWeekend = wd === 0 || wd === 6;
      const isHoliday = !!holidays[iso];
      // Today normally skips the grey weekend/holiday fill because it carries its own
      // marker. With hideToday there is no marker, so skipping it would leave today as
      // the one ungreyed weekend/holiday cell — i.e. still visibly highlighted.
      const offClass = (!isToday || hideToday) && (isWeekend || isHoliday) ? " mini-cal-off" : "";
      row.push(
        <button
          key={d}
          className={`mini-cal-day ${isToday && !hideToday ? "mini-cal-today" : ""}${offClass}${rangeClass(iso)}`}
          onClick={() => onSelect(viewYear, viewMonth, d)}
          title={isHoliday ? holidays[iso] : undefined}
        >{d}</button>
      );
    }
    // Fill remaining cells if last row is incomplete
    while (row.length < 7) {
      row.push(<span key={`empty-end-${row.length}`} className="mini-cal-empty" />);
    }
    rows.push({ week: getISOWeek(new Date(viewYear, viewMonth, rowStart)), cells: row });
  }

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else { setViewMonth(viewMonth - 1); }
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else { setViewMonth(viewMonth + 1); }
  };

  return (
    <div className="mini-cal" ref={ref}>
      <div className="mini-cal-header">
        <button className="mini-cal-nav" onClick={prevMonth}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>
        <button className="mini-cal-title mini-cal-title-btn" onClick={() => setView("months")}>
          {MONTH_NAMES[viewMonth]} {viewYear}
        </button>
        <button className="mini-cal-nav" onClick={nextMonth}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></button>
      </div>
      <div className="mini-cal-weekdays">
        <span />
        {WEEKDAYS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="mini-cal-grid">
        {rows.map((row) => (
          <React.Fragment key={`week-${row.week}`}>
            <span className="mini-cal-week">{row.week}</span>
            {row.cells}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
